import React, { useState, useEffect } from "react";
import { Box, Typography, InputBase, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCurrentYear } from "../../../../../../utils/date";
import UBLPoolModal from "./UBLPoolModal";

const StatCard = ({ value, label, color }) => {
  const theme = useTheme();
  const t = theme.custom;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <Typography sx={{
        fontFamily: "'Inter', sans-serif",
        fontSize: "28px", fontWeight: 700,
        color: color || t.accent,
        lineHeight: 1, letterSpacing: "-0.02em",
      }}>
        {value}
      </Typography>
      <Typography sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "9px", letterSpacing: "0.12em",
        textTransform: "uppercase", color: t.text3,
      }}>
        {label}
      </Typography>
    </Box>
  );
};

const StatsBanner = ({ stats, cap, totalCap, onCapChange, readOnly = false }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const [capInput,   setCapInput]   = useState(String(cap?.ceiling ?? ""));
  const [modalOpen,  setModalOpen]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [saveError,  setSaveError]  = useState(false);

  // Sync input when cap data arrives from API
  useEffect(() => {
    if (cap?.ceiling != null) {
      setCapInput(String(cap.ceiling));
    }
  }, [cap]);

  const getFY = () => {
    const now = new Date();
    return now.getMonth() >= 3 ? getCurrentYear() + 1 : getCurrentYear();
  };

  const getCurrentQuarterStr = () => {
    const now = new Date();
    const q   = Math.ceil((now.getMonth() + 1) / 3);
    return `Q${q}-${getFY()}`;
  };

  const handleSet = async () => {
    const ceiling = Number(capInput);
    if (!capInput || isNaN(ceiling)) return;

    try {
      setSaving(true);
      setSaved(false);
      setSaveError(false);

      await onCapChange?.({ quarter: getCurrentQuarterStr(), ceiling });

      // Flash success state for 2 seconds
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setSaveError(true);
      setTimeout(() => setSaveError(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  // Derive button appearance from state
  const btnLabel    = saving ? "" : saved ? "Saved" : saveError ? "Error" : "Set";
  const btnBg       = saved
    ? "rgba(40,179,180,0.15)"
    : saveError
    ? "rgba(200,64,64,0.12)"
    : t.accent2;
  const btnBorder   = saved
    ? `1px solid ${t.accent}66`
    : saveError
    ? "1px solid rgba(200,64,64,0.35)"
    : `1px solid ${t.accent}44`;
  const btnColor    = saved
    ? t.accent
    : saveError
    ? "#c84040"
    : t.accent;

  return (
    <>
      <Box sx={{
        display: "flex", alignItems: "stretch",
        background: t.surface, border: `1px solid ${t.border2}`,
        borderRadius: "6px", overflow: "hidden",
      }}>

        {/* Stats section */}
        <Box sx={{
          display: "flex", alignItems: "center",
          gap: "32px", padding: "16px 24px",
          borderRight: `1px solid ${t.border2}`, flex: 1,
        }}>
          <Box>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "9px", letterSpacing: "0.16em",
              textTransform: "uppercase", color: t.text3, mb: "10px",
            }}>
              Unbilled Position Cap · FY {getFY()}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: "28px" }}>

              {/* Unbilled HC — clickable, opens UBL pool modal */}
            <Box
  onClick={readOnly ? undefined : () => setModalOpen(true)}
  sx={{
    cursor: readOnly ? "default" : "pointer", borderRadius: "4px",
    padding: "2px 4px", transition: "background 0.15s",
    "&:hover": { background: readOnly ? "transparent" : t.accent2 },
  }}
>
              <StatCard
    value={stats?.unbilledHC ?? "—"}    // ← was cap?.ceiling
    label="Total Unbilled HC Cap"
    color={t.accent}
  />
            </Box>

              <StatCard 
  value={stats ? (stats.allocToMAC ?? 0) + (stats.forwardClaimed ?? 0) : "—"} 
  label="Alloc to MAC" 
  color={t.gold} 
/>
              <StatCard value={stats?.inSCGPool      ?? "—"} label="In SCG Pool"     color={t.accent} />
              {/* <StatCard value={stats?.impactTrainees ?? "—"} label="Impact Trainees" color={t.text2}  /> */}
            </Box>
          </Box>
        </Box>

        {/* FY Cap setter */}
{!readOnly && <Box sx={{
  display: "flex", flexDirection: "column",
  justifyContent: "center", padding: "14px 20px",
  gap: "8px", minWidth: "170px",
  background: t.surface2,
}}>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px", letterSpacing: "0.14em",
            textTransform: "uppercase", color: t.text3,
          }}>
            FY {getFY()} Cap
          </Typography>

          <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {/* Number input */}
            <Box sx={{
              width: "52px", height: "32px",
              background: t.surface3,
              border: `1px solid ${saved ? t.accent + "66" : saveError ? "rgba(200,64,64,0.35)" : t.border2}`,
              borderRadius: "4px",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "border-color 0.2s",
            }}>
              <InputBase
                value={capInput}
                onChange={(e) => {
                  setCapInput(e.target.value.replace(/\D/, ""));
                  setSaved(false);
                  setSaveError(false);
                }}
                disabled={saving}
                sx={{
                  width: "100%",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "14px", fontWeight: 600,
                  color: t.text,
                  "& input": { textAlign: "center", padding: 0 },
                }}
              />
            </Box>

            {/* Set button */}
            <Box
              onClick={saving ? undefined : handleSet}
              sx={{
                height: "32px", px: "14px",
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: "5px",
                background: btnBg,
                border: btnBorder,
                borderRadius: "4px",
                cursor: saving ? "not-allowed" : "pointer",
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px", fontWeight: 600,
                color: btnColor,
                transition: "all 0.2s",
                minWidth: "48px",
                "&:hover": saving || saved || saveError ? {} : {
                  background: t.accent,
                  color: isDark ? "#060E0D" : "#fff",
                },
              }}
            >
              {saving
                ? <CircularProgress size={10} sx={{ color: t.accent }} />
                : btnLabel
              }
            </Box>
          </Box>

          {/* Set by info or inline feedback message */}
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "9px", letterSpacing: "0.04em",
            color: saved ? t.accent : saveError ? "#c84040" : t.text3,
            transition: "color 0.2s",
          }}>
            {saved
              ? `Ceiling updated to ${capInput}`
              : saveError
              ? "Failed to set — try again"
              : cap
              ? `Set by ${cap.setByUserName ?? "—"} · ${new Date(cap.setAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`
              : "Not set yet"
            }
          </Typography>

          <Typography
            onClick={() => setModalOpen(true)}
            sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "9px", color: t.accent,
              cursor: "pointer", letterSpacing: "0.04em",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Manage UBL Pool →
          </Typography>
        </Box>}
      </Box>

      {!readOnly && modalOpen && <UBLPoolModal onClose={() => setModalOpen(false)} />}

      {modalOpen && <UBLPoolModal onClose={() => setModalOpen(false)} />}
    </>
  );
};

export default StatsBanner;